import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { auth } from "./firebase";

const BASE = import.meta.env.VITE_API_BASE ?? "/api";

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.message);
    this.name = "ApiClientError";
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: { accept: "application/json" } });
  const json = (await res.json()) as unknown;
  if (!res.ok) {
    const err = (json as { error?: ApiError }).error ?? {
      code: "INTERNAL",
      message: "Unknown error",
    };
    throw new ApiClientError(res.status, err);
  }
  return json as T;
}

export interface HealthResponse {
  ok: boolean;
  db: { categories: number };
}

export const getHealth = () => apiGet<HealthResponse>("/health");

/** Current Firebase ID token, or null when signed out / unconfigured. */
export async function getIdToken(): Promise<string | null> {
  const user = auth?.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

async function authedRequest<T>(path: string, method: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  const headers: Record<string, string> = { accept: "application/json" };
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers["content-type"] = "application/json";

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    const err = (json as { error?: ApiError }).error ?? {
      code: "INTERNAL",
      message: "Unknown error",
    };
    throw new ApiClientError(res.status, err);
  }
  return json as T;
}

export const apiPost = <T>(path: string, body?: unknown) => authedRequest<T>(path, "POST", body);
export const apiPatch = <T>(path: string, body?: unknown) => authedRequest<T>(path, "PATCH", body);

export interface MeUser {
  uid: string;
  email: string;
  nickname: string;
  avatarKey: string | null;
  avatarUrl: string | null;
  bio: string;
  role: "user" | "sub_admin" | "admin";
  emailVerified: boolean;
  createdAt: number;
  updatedAt: number;
}

/** React Query hook for the current user's profile (GET /api/me). Enable when signed in. */
export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: ["me"],
    enabled,
    queryFn: async () => {
      const token = await getIdToken();
      const headers: Record<string, string> = { accept: "application/json" };
      if (token) headers.authorization = `Bearer ${token}`;
      const res = await fetch(`${BASE}/me`, { headers });
      const json = (await res.json().catch(() => ({}))) as unknown;
      if (!res.ok) {
        const err = (json as { error?: ApiError }).error ?? {
          code: "INTERNAL",
          message: "Unknown error",
        };
        throw new ApiClientError(res.status, err);
      }
      return (json as { user: MeUser }).user;
    },
  });
}

export interface PublicUser {
  uid: string;
  nickname: string;
  avatarKey: string | null;
  avatarUrl: string | null;
  bio: string;
  role: "user" | "sub_admin" | "admin";
  createdAt: number;
}

export interface AdminUsersResponse {
  users: MeUser[];
  total: number;
  page: number;
  pageSize: number;
}

export const updateMyProfile = (patch: {
  nickname?: string;
  bio?: string;
  avatarKey?: string | null;
}) => authedRequest<{ user: MeUser }>("/me", "PATCH", patch).then((r) => r.user);

export const fetchPublicUser = (uid: string) =>
  authedRequest<{ user: PublicUser }>(`/users/${uid}`, "GET").then((r) => r.user);

export const fetchAdminUsers = (q: string, page: number) =>
  authedRequest<AdminUsersResponse>(`/admin/users?q=${encodeURIComponent(q)}&page=${page}`, "GET");

export const setUserRole = (uid: string, role: "user" | "sub_admin") =>
  authedRequest<{ user: MeUser }>(`/admin/users/${uid}`, "PATCH", { role }).then((r) => r.user);

/** React Query: another user's public profile. */
export function usePublicUser(uid: string) {
  return useQuery({
    queryKey: ["user", uid],
    queryFn: () => fetchPublicUser(uid),
    enabled: Boolean(uid),
  });
}

/** React Query mutation: update my nickname/bio; invalidates the cached profile. */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { nickname?: string; bio?: string; avatarKey?: string | null }) =>
      updateMyProfile(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

/** React Query: paginated admin user list. */
export function useAdminUsers(q: string, page: number) {
  return useQuery({ queryKey: ["admin-users", q, page], queryFn: () => fetchAdminUsers(q, page) });
}

/** React Query mutation: change a user's role; invalidates the admin list. */
export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { uid: string; role: "user" | "sub_admin" }) =>
      setUserRole(vars.uid, vars.role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
}

export interface Category {
  id: number;
  slug: string;
  name: string;
}

export interface PostCard {
  id: string;
  board: "public" | "admin";
  title: string;
  categoryId: number | null;
  categoryName: string | null;
  authorUid: string;
  authorNickname: string;
  thumbnailKey: string | null;
  thumbnailUrl: string | null;
  likeCount: number;
  commentCount: number;
  downloadCount: number;
  createdAt: number;
}

export interface PostFile {
  id: string;
  originalName: string;
  size: number;
  mime: string;
  storagePath: string;
}

export interface PostDetail extends PostCard {
  description: string;
  features: string;
  updatedAt: number;
  files: PostFile[];
  likedByMe: boolean;
}

export interface PostsPage {
  posts: PostCard[];
  total: number;
  page: number;
  pageSize: number;
}

export interface NewProjectInput {
  title: string;
  description: string;
  features: string;
  categoryId: number | null;
  board: "public" | "admin";
  thumbnailKey?: string | null;
  files?: Array<{ storagePath: string; originalName: string; size: number; mime: string }>;
}

export const fetchCategories = () =>
  authedRequest<{ categories: Category[] }>("/categories", "GET").then((r) => r.categories);

export const createProject = (input: NewProjectInput) =>
  authedRequest<{ id: string }>("/posts", "POST", input).then((r) => r.id);

export const fetchPosts = (opts: {
  board: "public" | "admin";
  categoryId?: number;
  page: number;
  q?: string;
}) => {
  const params = new URLSearchParams({ board: opts.board });
  if (opts.categoryId !== undefined) params.set("category", String(opts.categoryId));
  params.set("page", String(opts.page));
  if (opts.q) params.set("q", opts.q);
  return authedRequest<PostsPage>(`/posts?${params.toString()}`, "GET");
};

export const fetchBestPosts = () =>
  authedRequest<{ posts: PostCard[] }>("/posts/best", "GET").then((r) => r.posts);

export const fetchPost = (id: string) =>
  authedRequest<{ post: PostDetail }>(`/posts/${id}`, "GET").then((r) => r.post);

export const deleteProject = (id: string) => authedRequest<{ ok: true }>(`/posts/${id}`, "DELETE");

export function useCategories() {
  return useQuery({ queryKey: ["categories"], queryFn: fetchCategories, staleTime: 5 * 60_000 });
}

export function usePosts(opts: {
  board: "public" | "admin";
  categoryId?: number;
  page: number;
  q?: string;
}) {
  return useQuery({
    queryKey: ["posts", opts.board, opts.categoryId ?? null, opts.page, opts.q ?? ""],
    queryFn: () => fetchPosts(opts),
  });
}

export function useBestPosts() {
  return useQuery({ queryKey: ["posts", "best"], queryFn: fetchBestPosts });
}

export function usePost(id: string) {
  return useQuery({ queryKey: ["post", id], queryFn: () => fetchPost(id), enabled: Boolean(id) });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewProjectInput) => createProject(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posts"] }),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posts"] }),
  });
}

export interface UpdateProjectInput {
  title?: string;
  description?: string;
  features?: string;
  categoryId?: number | null;
  thumbnailKey?: string | null;
  removeFileIds?: string[];
  addFiles?: NewProjectInput["files"];
}

export const updateProject = (id: string, patch: UpdateProjectInput) =>
  authedRequest<{ post: PostDetail }>(`/posts/${id}`, "PATCH", patch).then((r) => r.post);

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateProjectInput) => updateProject(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["post", id] });
      qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });
}

export const incrementDownload = (postId: string) =>
  authedRequest<{ count: number }>(`/posts/${postId}/downloads`, "POST");

export interface Comment {
  id: string;
  authorUid: string;
  authorNickname: string;
  avatarUrl?: string | null;
  body: string;
  likeCount: number;
  likedByMe: boolean;
  createdAt: number;
}

export const toggleLike = (targetType: "post" | "comment", targetId: string) =>
  authedRequest<{ liked: boolean; count: number }>("/likes", "POST", { targetType, targetId });

export const fetchComments = (postId: string) =>
  authedRequest<{ comments: Comment[] }>(`/posts/${postId}/comments`, "GET").then(
    (r) => r.comments,
  );

export const addComment = (postId: string, body: string) =>
  authedRequest<{ comment: Comment }>(`/posts/${postId}/comments`, "POST", { body }).then(
    (r) => r.comment,
  );

export const deleteComment = (commentId: string) =>
  authedRequest<{ ok: true }>(`/comments/${commentId}`, "DELETE");

export function useComments(postId: string) {
  return useQuery({
    queryKey: ["comments", postId],
    queryFn: () => fetchComments(postId),
    enabled: Boolean(postId),
  });
}

export function useToggleLike(postId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { targetType: "post" | "comment"; targetId: string }) =>
      toggleLike(vars.targetType, vars.targetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["post", postId] });
      qc.invalidateQueries({ queryKey: ["comments", postId] });
    },
  });
}

export function useAddComment(postId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => addComment(postId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comments", postId] });
      qc.invalidateQueries({ queryKey: ["post", postId] });
    },
  });
}

export function useDeleteComment(postId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteComment(commentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comments", postId] });
      qc.invalidateQueries({ queryKey: ["post", postId] });
    },
  });
}

export function useIncrementDownload(postId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => incrementDownload(postId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["post", postId] }),
  });
}
